// src/components/CarriagePanel.js
//
// Record 594 (ruling 135 audit) - AUDIT_RULINGS_111-170.md FIX 2 (rulings 135, 155; makes 143 reachable).
// CP-1's server half (functions\index.js exports.carriageAct, src\data\carriage.js) was built and correct,
// but no screen called it and nothing rendered carriageLine - so carriage could never move and the member
// could not see who carries her claim (ruling 29). This panel is that missing screen half:
//
//   - reads claims/{id}/carriageActs (firestore.rules: createdBy, claimantUid, admin, in-scope officers on a
//     filed claim - the claimant's read arm already exists, so no rule changes with this panel);
//   - shows carriageLine(carriageOf(acts, ...)) - who carries the claim now;
//   - offers THE CLAIMANT, and only her, "Submit to my representative for furtherance to the carrier" and
//     "Give for review only" (ruling 135: her act, and the purpose qualifies it);
//   - offers "Return carriage" to a party mayAct admits (ruling 143: each party agrees under its own uid;
//     the function writes the act only when the set is complete).
//
// WHO THE CLAIMANT IS - matched EXACTLY as carriageAct matches her: claim.claimantUid === the caller's uid, and
// nothing else. No createdBy fallback, no e-mail match: the function refuses a claim with no claimantUid
// ("the platform will not guess which account that is"), so this screen says so instead of offering a button
// the server would refuse.
//
// WHO THE REPRESENTATIVE IS - the holder of record of the Local Chairperson seat for the claim's scopeKey, read
// from the seats spine (offices/ + officeTerms/, readable by any signed-in account) through the roster the page
// already holds (useSeatsOfRecord). Where that seat has no holder uid on record - unrouted claim, no seat, a
// vacancy, or a committee still on the bundled roster, which carries no uids - the control is shown DISABLED
// with the reason. The platform does not guess a representative any more than it guesses a claimant.
//
// DEFAULT - Record 609 (ruling 294, Bob 2026-10-08: "Record by default"; ruling 135): the furtherance submission to the
// Local Chairperson of record is recorded by default at filing, ONCE, by src\data\carriageAtFiling.js, which the three
// filing paths (NewClaim, QuickFile, ClaimDetail draft -> submitted) call after the claim write has succeeded. The
// member may specify otherwise here: give it for review only, or return carriage. THIS panel never auto-submits -
// there is deliberately no effect or timer in this file that calls carriageAct; it moves only on her click.
//
// Errors are shown on screen, never swallowed: a failed read is NOT "you carry this claim".
import React, { useEffect, useMemo, useState } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  SUBMITTED, RETURNED, WITHDRAWN, RELEASED, REFILED, FURTHERANCE, FOR_REVIEW, REPRESENTATIVE,
  carriageOf, carriageLine, mayAct, purposeExplanation, WITHDRAW_EXPLANATION, RELEASE_EXPLANATION,
  isClaimOfficer, refiledLine,
} from '../data/carriage';
import RulingRef from './RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import { representativeFor } from '../data/carriageAtFiling';   // Record 609 (ruling 294): one representative test

const C = {
  line: '#1E3A52', panel: '#0F1E2E', text: '#F4F6F8', dim: '#8AA4BF',
  accent: '#E8A020', ok: '#27AE60', bad: '#C0392B',
};

// Ruling 168: the reasons carriage.js and the function give carry "(ruling NN)" inline. A member sees plain
// language with the number behind Why?, so the inline form is taken out here and the RulingRef carries it.
const plain = (t) => String(t || '').replace(/\s*\((?:rulings?|Ruling)\s[^)]*\)/g, '').trim();

// The representative this claim would go to: the Local Chairperson seat of record for its scopeKey. Record 609
// (ruling 294): moved to src\data\carriageAtFiling.js so the filing default uses the same test; re-exported here.
export { representativeFor };

export default function CarriagePanel({ claim, claimId, user, roster, rosterSource }) {
  const [acts, setActs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [readErr, setReadErr] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [pending, setPending] = useState('');      // the purpose she has chosen and not yet confirmed
  const [specified, setSpecified] = useState('');

  const id = claimId || claim?.docId || claim?.id;
  const isDraft = String(claim?.status || '') === 'draft';

  useEffect(() => {
    if (!id || isDraft) return undefined;
    // `at` is the server's ISO string (carriageAct writes nowIso), so it orders as written.
    const q = query(collection(db, 'claims', id, 'carriageActs'), orderBy('at', 'asc'));
    return onSnapshot(q,
      (snap) => { setActs(snap.docs.map((d) => ({ ...d.data(), docId: d.id }))); setReadErr(''); setLoaded(true); },
      (e) => {
        setReadErr('Could not read the carriage record: ' + (e?.code || e?.message || e)
          + '. This is NOT "you carry this claim" - who carries it is unknown until the record can be read.');
        setLoaded(true);
      });
  }, [id, isDraft]);

  const who = useMemo(() => ({
    claimantUid: String(claim?.claimantUid || ''),
    claimantName: String(claim?.claimantName || ''),
  }), [claim?.claimantUid, claim?.claimantName]);
  const state = useMemo(() => carriageOf(acts, who), [acts, who]);
  // Ruling 307: the claim's committee - the same field carriageAct reads - decides whose General Chairperson may release it.
  const committeeId = String(claim?.committee || '');
  const [confirming, setConfirming] = useState('');   // WITHDRAWN or RELEASED while its explanation is on screen
  const [refileNo, setRefileNo] = useState('');       // ruling 307 item 3: the number of the union's new filing

  // Carriage on a draft: ruling 56 - nobody but its author and the administrator reads a draft, so a
  // representative could not read what was submitted to him. The panel appears once the claim is filed.
  if (!id || isDraft) return null;

  const uid = String((user && user.uid) || (auth.currentUser && auth.currentUser.uid) || '');
  const isCarriageClaimant = !!uid && !!who.claimantUid && who.claimantUid === uid;   // exactly carriageAct's test
  const rep = representativeFor(claim, roster, rosterSource);
  const submitMay = mayAct(SUBMITTED, { actorUid: uid, claimantUid: who.claimantUid, state });
  // Ruling 307: the member withdraws her submission (hers alone); the claim's General Chairperson releases it (hers alone).
  // The same carriage.js mayAct() the carriageAct function decides with.
  const actorObj = { uid, role: user && user.role, committee: user && user.committee };
  const withdrawMay = mayAct(WITHDRAWN, { actorUid: uid, claimantUid: who.claimantUid, state, actor: actorObj, committeeId });
  const releaseMay = mayAct(RELEASED, { actorUid: uid, claimantUid: who.claimantUid, state, actor: actorObj, committeeId });
  const alreadyWithRep = state.holder === REPRESENTATIVE && rep.uid && state.holderUid === rep.uid;

  let submitBlocked = '';
  if (!who.claimantUid) {
    submitBlocked = 'This claim does not identify its claimant as a platform account, so carriage cannot move on it. '
      + 'The claimant must be linked to the claim first.';
  } else if (!rep.uid) submitBlocked = rep.reason;
  else if (rep.uid === uid) submitBlocked = 'You hold the Local Chairperson seat for this claim yourself; there is no other representative to submit it to.';

  const call = async (payload, label) => {
    setErr(''); setMsg(''); setBusy(label);
    try {
      const fn = httpsCallable(getFunctions(), 'carriageAct');
      const r = await fn(payload);
      const d = (r && r.data) || {};
      if (payload.kind === WITHDRAWN) {
        setMsg('Recorded. You withdrew your submission; you carry this claim. The union has been told, and may still pursue it.');
      } else if (payload.kind === RELEASED) {
        setMsg('Recorded. The claim is released to the member; she has been told.');
      } else if (payload.kind === REFILED) {
        setMsg('Recorded on both claims. The member has been told.'); setRefileNo('');
      } else if (payload.kind === SUBMITTED) {
        setMsg(d.moved
          ? 'Recorded. ' + (d.carriedByName || 'Your representative') + ' now carries this claim.'
          : 'Recorded. Who carries this claim has not changed.');
      } else {
        setMsg('Recorded.');
      }
      setPending(''); setSpecified(''); setConfirming('');
    } catch (e) {
      setErr('Refused: ' + plain(e?.message || String(e)));
    } finally { setBusy(''); }
  };

  // ONLY EVER CALLED FROM A CLICK (the Confirm button below). Record 594 / 609: this panel never auto-submits; the
  // filing default (ruling 294) is recorded once by src\data\carriageAtFiling.js from the filing paths.
  const submit = (purpose) => call({
    claimId: id, kind: SUBMITTED, purpose, toUid: rep.uid, specified: specified.trim(),
  }, 'submit');
  const giveBack = (kind) => call({ claimId: id, kind }, 'return');
  // Ruling 307 item 3: an officer links the union's own filing - also only from its button.
  const refile = (number) => call({ claimId: id, kind: REFILED, newClaimNumber: number }, 'refile');

  const btn = (on) => ({
    background: 'none', border: '1px solid ' + (on ? C.accent : C.line), color: on ? C.text : C.dim,
    padding: '6px 12px', fontSize: 12, cursor: on ? 'pointer' : 'default', marginRight: 8, marginTop: 6,
    opacity: on ? 1 : 0.6,
  });

  const shown = acts.filter((a) => a && [SUBMITTED, RETURNED, WITHDRAWN, RELEASED, REFILED].includes(a.act));
  // Ruling 307 item 3: after her withdrawal an officer of the claim links the union's own filing of it. The server decides
  // (carriage.mayRecordRefiling); this only offers the box to an officer of the claim while her withdrawal stands.
  const officerHere = isClaimOfficer({ uid, role: user && user.role, committee: user && user.committee, scopes: (user && user.scopes) || [] },
    committeeId, claim?.scopeKey);
  const mayRefile = officerHere && state.act && state.act.act === WITHDRAWN;

  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.line }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
          Who carries this claim
          <RulingRef user={user} n={135} why="Carriage moves when the claimant submits her claim to her representative for furtherance to the carrier, unless she specifies otherwise. It never moves because an officer picked the matter up." />
        </div>
      </div>

      <div style={{ padding: '10px 14px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>
        {readErr && <div style={{ color: C.bad, marginBottom: 8 }}>⚠ {readErr}</div>}
        {err && <div style={{ color: C.bad, marginBottom: 8 }}>⚠ {err}</div>}
        {msg && <div style={{ color: C.ok, marginBottom: 8 }}>{msg}</div>}

        {!readErr && (loaded
          ? <div style={{ color: C.text, fontSize: 13, marginBottom: 8 }}>{carriageLine(state, { claimantName: who.claimantName })}</div>
          : <div style={{ marginBottom: 8 }}>Reading the carriage record...</div>)}

        {!readErr && loaded && shown.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            {shown.map((a) => (
              <div key={a.docId} style={{ padding: '4px 0', borderBottom: '1px dotted ' + C.line }}>
                <span style={{ fontWeight: 700, color: a.act === SUBMITTED ? C.accent : C.dim }}>
                  {a.act === RETURNED ? 'RETURNED TO THE CLAIMANT'
                    : a.act === REFILED ? 'FILED AGAIN BY THE UNION'
                    : a.act === WITHDRAWN ? 'SUBMISSION WITHDRAWN BY THE MEMBER'
                    : a.act === RELEASED ? 'RELEASED TO THE MEMBER BY THE GENERAL CHAIRPERSON'
                    : a.purpose === FURTHERANCE ? 'SUBMITTED FOR FURTHERANCE' : 'GIVEN FOR REVIEW ONLY'}
                </span>
                <span>{'  -  ' + (a.at || '') + '  -  ' + (a.actorName || a.actorEmail || '')
                  + (a.act === SUBMITTED && a.toName ? '  ->  ' + a.toName : '')}</span>
                {a.specified ? <div style={{ color: C.text, fontStyle: 'italic', marginTop: 2 }}>“{a.specified}”</div> : null}
                {a.act === REFILED ? <div style={{ color: C.text, marginTop: 2 }}>{refiledLine(a)}</div> : null}
              </div>
            ))}
          </div>
        )}

        {/* THE CLAIMANT'S ACT (ruling 135). Offered to her alone; an officer is never offered it. */}
        {isCarriageClaimant && !readErr && loaded && submitMay.may && (
          <div style={{ marginTop: 6 }}>
            {submitBlocked && (
              <div style={{ color: C.accent, marginBottom: 6 }}>Submitting is not available: {submitBlocked}</div>
            )}
            {pending ? (
              <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: '10px 12px', color: C.text }}>
                <div style={{ marginBottom: 8 }}>{purposeExplanation(pending)}</div>
                <div style={{ color: C.dim, marginBottom: 4 }}>
                  Going to: {rep.name || 'the Local Chairperson of record'}
                </div>
                <label className="form-label" htmlFor="carriage-specified" style={{ color: C.dim }}>
                  Anything you specify about this hand-over (optional, in your own words)
                </label>
                <textarea id="carriage-specified" className="form-input" rows={3} value={specified}
                  onChange={(e) => setSpecified(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
                <button type="button" onClick={() => submit(pending)} disabled={!!busy || !!submitBlocked}
                  style={btn(!busy && !submitBlocked)}>
                  {busy === 'submit' ? 'Recording...' : 'Confirm'}
                </button>
                <button type="button" onClick={() => { setPending(''); setErr(''); }} disabled={!!busy} style={btn(!busy)}>
                  Cancel
                </button>
              </div>
            ) : (
              <div>
                <button type="button" onClick={() => { setMsg(''); setPending(FURTHERANCE); }}
                  disabled={!!busy || !!submitBlocked || !!alreadyWithRep} style={btn(!busy && !submitBlocked && !alreadyWithRep)}
                  title={alreadyWithRep ? 'Your representative already carries this claim.' : submitBlocked}>
                  Submit to my representative for furtherance to the carrier
                </button>
                <button type="button" onClick={() => { setMsg(''); setPending(FOR_REVIEW); }}
                  disabled={!!busy || !!submitBlocked} style={btn(!busy && !submitBlocked)} title={submitBlocked}>
                  Give for review only
                </button>
                {alreadyWithRep && <div style={{ marginTop: 4 }}>Your representative already carries this claim.</div>}
              </div>
            )}
          </div>
        )}

        {isCarriageClaimant === false && !who.claimantUid && uid && claim?.createdBy === uid && (
          <div style={{ color: C.accent, marginTop: 6 }}>
            This claim does not identify its claimant as a platform account, so carriage cannot move on it.
          </div>
        )}

        {/* RULING 307: the member withdraws HER submission; the General Chairperson of the claim's committee releases the claim.
            Each is one person's own act, confirmed after its plain explanation, and recorded with the offices told. */}
        {!readErr && loaded && state.holder === REPRESENTATIVE && (withdrawMay.may || releaseMay.may) && (
          <div style={{ marginTop: 10 }}>
            {confirming ? (
              <div style={{ border: '1px solid ' + C.line, borderRadius: 6, padding: '10px 12px', color: C.text }}>
                <div style={{ marginBottom: 8 }}>{confirming === WITHDRAWN ? WITHDRAW_EXPLANATION : RELEASE_EXPLANATION}</div>
                <button type="button" onClick={() => giveBack(confirming)} disabled={!!busy} style={btn(!busy)}>
                  {busy === 'return' ? 'Recording...' : confirming === WITHDRAWN ? 'Withdraw my submission' : 'Release this claim to the member'}
                </button>
                <button type="button" onClick={() => { setConfirming(''); setErr(''); }} disabled={!!busy} style={btn(!busy)}>Cancel</button>
              </div>
            ) : (
              <div>
                {withdrawMay.may && (
                  <button type="button" onClick={() => { setMsg(''); setConfirming(WITHDRAWN); }} disabled={!!busy} style={btn(!busy)}>
                    Withdraw my submission
                  </button>
                )}
                {releaseMay.may && (
                  <button type="button" onClick={() => { setMsg(''); setConfirming(RELEASED); }} disabled={!!busy} style={btn(!busy)}>
                    Release this claim to the member
                  </button>
                )}
                <RulingRef user={user} n={307} why="Once submitted, the claim is the general committee's: the member may withdraw her own submission, and only the General Chairperson may release the claim. The union may still pursue the same claim." />
              </div>
            )}
          </div>
        )}

        {/* RULING 307 item 3 with 307.1: the union's own filing of the same claim, linked on both claims and told to her. */}
        {!readErr && loaded && mayRefile && (
          <div style={{ marginTop: 10, border: '1px solid ' + C.line, borderRadius: 6, padding: '10px 12px' }}>
            <div style={{ color: C.text, marginBottom: 6 }}>
              The member withdrew her submission. If the union has filed the same claim again - for her or for any member it
              affects - enter that claim's number to link the two. Both claims record it, and she is told.
            </div>
            <input className="form-input" value={refileNo} onChange={(e) => setRefileNo(e.target.value)}
              placeholder="The union's claim number" aria-label="The union's claim number" style={{ maxWidth: 260, marginRight: 8 }} />
            <button type="button" disabled={!!busy || !refileNo.trim()} style={btn(!busy && !!refileNo.trim())}
              onClick={() => refile(refileNo.trim())}>
              {busy === 'refile' ? 'Recording...' : 'Link the union\'s filing'}
            </button>
            <RulingRef user={user} n={307} why="After the member withdraws, the union may file the same claim again, for her or any member it affects; both acts are recorded." />
          </div>
        )}
      </div>
    </div>
  );
}
